// frontend/src/components/ExpressionEditor/SeriesConfigDialog.tsx
import React, { useState, useEffect } from 'react';

type Props = {
  isOpen: boolean;
  seriesLabel: string;
  isOHLC: boolean;
  compact?: boolean;
  initialField?: string;
  initialOffset?: number;
  expectedType?: 'number' | 'string' | null;
  functionId?: string | null;
  existingTypes?: string[];
  /** When set, used as the list of fields for indicator series (e.g. ['Lower', 'SMA', 'Upper'] for Bollinger) */
  availableFields?: string[];
  /** Preferred default field when opening for a new indicator (e.g. 'Lower' for Bollinger) */
  defaultField?: string;
  onSubmit: (config: { field?: string; offset: number }) => void;
  onCancel: () => void;
};

const SeriesConfigDialog: React.FC<Props> = ({
  isOpen,
  seriesLabel,
  isOHLC,
  compact = false,
  initialField,
  initialOffset = 0,
  expectedType,
  functionId: _functionId,
  existingTypes: _existingTypes,
  availableFields: availableFieldsProp,
  defaultField: defaultFieldProp,
  onSubmit,
  onCancel,
}) => {
  const [selectedField, setSelectedField] = useState<string>(initialField || '');
  const [offset, setOffset] = useState(initialOffset);

  
  useEffect(() => {
    if (!isOpen) return;

    // Reset offset
    setOffset(initialOffset ?? 0);

    // === EDITING EXISTING SERIES → preserve current field ===
    if (initialField) {
      setSelectedField(initialField);
      return;
    }

    // === NEW SERIES (no initialField) → apply smart default ===
    if (compact) {
      // Compact mode never has a field
      setSelectedField('');
      return;
    }

    // Compute available fields (same logic as rendering)
    const availableFields = (() => {
      if (isOHLC) {
        if (expectedType === 'string') return ['date'];
        return ['open', 'high', 'low', 'close', 'volume'];
      }
      // Indicator: use prop if provided (e.g. ['Lower', 'SMA', 'Upper']), else fallback
      if (availableFieldsProp && availableFieldsProp.length > 0) {
        return expectedType === 'string'
          ? availableFieldsProp.filter(f => f.toLowerCase() === 'date')
          : availableFieldsProp;
      }
      return expectedType === 'string' ? [] : ['Value'];
    })();

    // Apply default: per-indicator preferred field first, then historical rules
    if (defaultFieldProp && availableFields.includes(defaultFieldProp)) {
      setSelectedField(defaultFieldProp);
    } else if (availableFields.length === 1) {
      setSelectedField(availableFields[0]);
    } else if (availableFields.includes('close')) {
      setSelectedField('close');
    } else if (availableFields.includes('Value')) {
      setSelectedField('Value');
    } else if (availableFields.includes('SMA')) {
      setSelectedField('SMA');
    } else if (availableFields.length > 0) {
      setSelectedField(availableFields[0]);
    } else {
      setSelectedField('');
    }
  }, [
    isOpen,
    initialField,
    initialOffset,
    compact,
    isOHLC,
    expectedType,
    availableFieldsProp,
    defaultFieldProp,
  ]);

  if (!isOpen) return null;

  // COMPACT MODE — ONLY offset, NO field selection, OK always enabled
  if (compact) {
    // Inside compact block
    console.log('[SeriesConfigDialog] Rendering COMPACT mode');
    return (
      <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
        <div className="bg-newsprint-panel border border-rule rounded-sm p-8 w-96">
          <h2 className="text-xl font-bold text-ink mb-6 font-headline">
            Bar Offset for {seriesLabel}
          </h2>
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-ink mb-3">
                Days Back (0 = current bar)
              </label>
              <input
                type="number"
                min={0}
                value={offset}
                onChange={(e) => setOffset(Number(e.target.value))}
                className="w-full px-5 py-4 border-2 border-rule rounded-sm text-2xl font-medium focus:outline-none focus:border-ink focus:ring-4 focus:ring-newsprint-dark bg-newsprint-panel text-ink"
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-4">
              <button
                onClick={onCancel}
                className="px-6 py-3 text-base font-medium rounded-sm bg-newsprint-dark hover:bg-newsprint transition text-ink"
              >
                Cancel
              </button>
              <button
                onClick={() => onSubmit({ offset })}
                className="px-8 py-3 text-base font-medium rounded-sm bg-ink text-newsprint hover:opacity-90 transition"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // FULL MODE — field picker + offset
  const fields = (() => {
    if (compact) return [];

    if (isOHLC) {
      if (expectedType === 'string') return ['date'];
      return ['open', 'high', 'low', 'close', 'volume'];
    }

    // Indicator series: use availableFields prop when provided
    if (availableFieldsProp && availableFieldsProp.length > 0) {
      return expectedType === 'string'
        ? availableFieldsProp.filter(f => f.toLowerCase() === 'date')
        : availableFieldsProp;
    }
    return expectedType === 'string' ? [] : ['Value'];
  })();

  // Inside full block
  console.log('[SeriesConfigDialog] Rendering FULL mode');
  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
      <div className="bg-newsprint-panel border border-rule rounded-sm p-8 w-96">
        <h2 className="text-xl font-bold text-ink mb-6 font-headline">
          Configure Series: {seriesLabel}
        </h2>
        <div className="space-y-8">
          <div>
            <label className="block text-sm font-medium text-ink mb-4">
              Select Field
            </label>
            <div className="grid grid-cols-2 gap-3">
              {fields.map((field) => (
                <button
                  key={field}
                  onClick={() => setSelectedField(field)}
                  className={`py-4 px-6 rounded-sm border-2 font-medium text-base transition ${selectedField === field
                      ? 'bg-ink text-newsprint border-ink'
                      : 'bg-newsprint-panel text-ink border-rule hover:bg-newsprint-dark hover:border-ink/50'
                    }`}
                >
                  {field.charAt(0).toUpperCase() + field.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-ink mb-3">
              Series Lag (Days Back)
            </label>
            <input
              type="number"
              min={0}
              value={offset}
              onChange={(e) => setOffset(Number(e.target.value))}
              className="w-full px-5 py-4 border-2 border-rule rounded-sm text-2xl font-medium focus:outline-none focus:border-ink focus:ring-4 focus:ring-newsprint-dark bg-newsprint-panel text-ink"
            />
          </div>

          <div className="flex justify-end gap-4">
            <button
              onClick={onCancel}
              className="px-6 py-3 text-base font-medium rounded-sm bg-newsprint-dark hover:bg-newsprint transition text-ink"
            >
              Cancel
            </button>
            <button
              onClick={() => onSubmit({ field: selectedField, offset })}
              disabled={!selectedField}
              className="px-8 py-3 text-base font-medium rounded-sm bg-ink text-newsprint hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              OK
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SeriesConfigDialog;